import React from 'react';
import PropTypes from 'prop-types';
import '../../../polyfills/polyfill-allSettled';
import ContainerWrapper from '../../styled components/ContainerWrapper';
import {
  deserializeYesplanAPIDate,
  isOnSameDate,
  serializeYesplanAPIDate,
  todayDateObject
} from '../../libs/dateTimeUtilities';
import { printInShortFormatOrAsRelativeDate } from '../../libs/dateTimePrinting';
import { getDefaultFilter, isNoFilter } from '../../libs/eventsFilters';
import EventInfoCard from '../EventInfoCard';
import CollapsibleListItem from '../../components/CollapsibleListItem/CollapsibleListItem';
import LoadingState from '../../styled components/LoadingState';
import { Body, Meta } from '../../styled components/Typography';
import { TitleHeaderForEventGroup } from './EventsWeek.style';
import Sticky from '../../styled components/Sticky';
import SVGIcon from '../../components/SVGIcon/SVGIcon';
import Button from '../../components/Button/Button';
import FormatSettingsContext from '../../components/FormatSettingsContext';

class EventsWeek extends React.Component {
  constructor(props) {
    super(props);
    this.calculateStickyPositionDayHeader =
      this.calculateStickyPositionDayHeader.bind(this);
    this.calculateStickyPositionTopMostGroupHeader =
      this.calculateStickyPositionTopMostGroupHeader.bind(this);
    this.calculateStickyPositionDirectGroupHeader =
      this.calculateStickyPositionDirectGroupHeader.bind(this);
    this.dayHeaderRef = React.createRef();
    this.topMostGroupHeaderRef = React.createRef();

    this.addToCollapsibleDayRefs = this.addToCollapsibleDayRefs.bind(this);
    this.collapsibleDayElRefs = React.createRef([]);
    this.collapsibleDayElRefs.current = [];

    this.state = {
      topMostGroupHeaderHeight: 0
    };
  }

  componentDidMount() {
    this.updateTopMostGroupHeaderHeight();
  }

  componentDidUpdate() {
    this.updateTopMostGroupHeaderHeight();
  }

  addToCollapsibleDayRefs(el) {
    if (el && !this.collapsibleDayElRefs.current.includes(el)) {
      this.collapsibleDayElRefs.current.push(el);
    }
  }

  updateTopMostGroupHeaderHeight() {
    const { topMostGroupHeaderHeight } = this.state;
    if (
      this.topMostGroupHeaderRef &&
      this.topMostGroupHeaderRef.current &&
      topMostGroupHeaderHeight === 0
    ) {
      const height = this.topMostGroupHeaderRef.current.clientHeight;
      /* Suppress the linting rule: it is allowed to call setState in componentDidMount (see React docs)
         and necessary here to ensure that the rendering uses the correct height of the element.
         This element is only available after mounting */
      /* eslint-disable-next-line  react/no-did-mount-set-state */
      if (height !== 0 && height !== topMostGroupHeaderHeight)
        this.setState({
          topMostGroupHeaderHeight: this.topMostGroupHeaderRef.current.clientHeight
        });
    }
  }

  calculateStickyPositionDayHeader() {
    const { headerRef, containerWrapperRef, stickyPosition } = this.props;
    if (
      headerRef &&
      headerRef.current &&
      containerWrapperRef &&
      containerWrapperRef.current
    )
      return stickyPosition + 1;
    return 0;
  }

  calculateStickyPositionTopMostGroupHeader() {
    const positionDayHeader = this.calculateStickyPositionDayHeader();
    return (
      positionDayHeader +
      (this.dayHeaderRef && this.dayHeaderRef.current
        ? this.dayHeaderRef.current.offsetHeight
        : 0)
    );
  }

  calculateStickyPositionDirectGroupHeader() {
    const { topMostGroupHeaderHeight } = this.state;
    const positionDayHeader = this.calculateStickyPositionTopMostGroupHeader();
    return positionDayHeader + topMostGroupHeaderHeight;
  }

  renderFetchMoreAction(dateObject) {
    if (this.props.hasDateMoreEventsToShow(dateObject)) {
      const nextNumberToShow = this.props.numberOfNextEventsToShow(dateObject);
      return (
        <>
          {this.props.hasFetchMoreError && (
            <Meta tw="inline-flex mb-8 py-4 pl-8 pr-16 bg-red-lighten-30">
              <span tw="mr-8">
                <SVGIcon icon="error" size="medium" tw="text-red-darken-10 block" />
              </span>
              <span>{`${i18n('Something went wrong')}...`}</span>
            </Meta>
          )}
          <div tw="mt-8 mb-16 ">
            <Button
              tw="mx-auto "
              contentType="textWithIcon"
              color="primary"
              variant="outlined"
              size="small"
              icon="expand-more"
              iconPosition="right"
              onClick={() => {
                this.props.showMoreEventsOnDate(dateObject);
              }}
            >
              {i18n`Show Next ${nextNumberToShow} Events`}
            </Button>
          </div>
        </>
      );
    }
    return null;
  }

  renderEventInfoCard(event, key) {
    return (
      <EventInfoCard
        eventInfo={event}
        {...this.props}
        key={`${key}-eventinfo-${event.id}`}
      />
    );
  }

  renderDirectGroupWithChildEvents(directGroupWithChildEvents, key, topMostGroup, index) {
    const isLastGroupOrDirectgroup = topMostGroup.isLastEventInGroup(index);
    let showDivider = !isLastGroupOrDirectgroup;
    const extraSpacingAboveSubgroup = index > 0;
    /* Show divider if a direct group is followed by ungrouped events
       No divider is needed when a direct group is followed by another direct group */
    if (!isLastGroupOrDirectgroup) {
      showDivider = !topMostGroup.getEventAtIndex(index + 1).isGroup();
    }

    return (
      <div key={key}>
        <div
          data-test-eventsubgroup={directGroupWithChildEvents.getGroupNameOrPlaceholder()}
          key={`${key}-subgroup-${directGroupWithChildEvents.id}`}
          tw="pl-32 mb-8 "
        >
          {extraSpacingAboveSubgroup && <div tw="pt-8 " />}
          <Sticky
            $zIndex={2}
            $top={this.calculateStickyPositionDirectGroupHeader()}
            ref={this.directGroupHeaderRef}
          >
            <TitleHeaderForEventGroup
              label={directGroupWithChildEvents.getGroupNameOrPlaceholder()}
            />
          </Sticky>
          <div tw="pr-16 space-y-8">
            {directGroupWithChildEvents.doForEachEventOrGroup(
              () => null,
              (event) => this.renderEventInfoCard(event, `${key}-subgroup`),
              true
            )}
          </div>
        </div>
        {showDivider && <div tw="pt-8 pb-8 " />}
      </div>
    );
  }

  renderTopMostGroupWithChildren(topMostGroupWithChildren, key, index, allEvents) {
    const isLastGroup = allEvents.isLastEventInStructure(index);
    let showDivider = !isLastGroup;
    if (!isLastGroup) {
      /* If top most group is followed by another top most group, don't show a divider */
      showDivider = !allEvents.isEventOrGroupFollowedByGroup(index + 1);
    }
    const topGroupedDirectlyFollowedBySubGroup =
      topMostGroupWithChildren.isFirstChildAGroup();

    return (
      <div
        data-test-eventgroup={topMostGroupWithChildren.getGroupNameOrPlaceholder()}
        key={key}
        tw="pl-16"
      >
        {index !== 0 && <div tw="pt-8 " />}
        <Sticky $zIndex={3} $top={this.calculateStickyPositionTopMostGroupHeader()}>
          <TitleHeaderForEventGroup
            ref={this.topMostGroupHeaderRef}
            label={topMostGroupWithChildren.getGroupNameOrPlaceholder()}
            tw=" ml-16  "
            followedByOtherHeader={topGroupedDirectlyFollowedBySubGroup}
          />
        </Sticky>
        <div tw="space-y-8 ">
          {topMostGroupWithChildren.doForEachEventOrGroup(
            (groupEvent, indexInTopMostGroup) =>
              this.renderDirectGroupWithChildEvents(
                groupEvent,
                `${key}-${groupEvent.id}`,
                topMostGroupWithChildren,
                indexInTopMostGroup
              ),
            (event) => (
              <div key={`${key}-${event.id}`}>
                <div tw="pr-16 space-y-8 pl-16  ">
                  {this.renderEventInfoCard(event, key)}
                </div>
              </div>
            ),
            true
          )}
        </div>
        {showDivider && <div tw="mx-16 mt-16 mb-8 pt-8 " />}
      </div>
    );
  }

  renderTopMostGroupsAndEvents(eventsStructure, key, limit) {
    return eventsStructure.doForEachEventOrGroup(
      (groupEvent, index) =>
        this.renderTopMostGroupWithChildren(
          groupEvent,
          `${key}-group-${groupEvent.id}`,
          index,
          eventsStructure
        ),
      (event, index) => {
        if (index === 0)
          return (
            <div key={`${key}-${event.id}`} tw="mb-8 pl-16 pr-16 space-y-8 pt-8">
              {this.renderEventInfoCard(event, key)}
            </div>
          );
        return (
          <div key={`${key}-${event.id}`} tw="mb-8 pl-16 pr-16 space-y-8">
            {this.renderEventInfoCard(event, key)}
          </div>
        );
      },
      limit
    );
  }

  renderEventsOfDate(dateObject, eventsStructure, key) {
    const { eventsFilterOptions } = this.props;
    if (!eventsStructure || eventsFilterOptions === null) {
      return (
        <div tw="pt-24 pb-20 relative z-1" key={key}>
          <LoadingState $size="small" />
        </div>
      );
    }
    if (eventsStructure.hasEvents()) {
      const dateString = serializeYesplanAPIDate(dateObject);
      const { numberOfShownEventsPerDate, isFetchingMoreEvents } = this.props;

      const isLimited = numberOfShownEventsPerDate[dateString] !== undefined;
      const limit = isLimited && numberOfShownEventsPerDate[dateString];
      return (
        <>
          {this.renderTopMostGroupsAndEvents(
            eventsStructure,
            `${key}-eventsByGroup`,
            limit
          )}
          {isFetchingMoreEvents ? (
            <div tw="pt-16 pb-20 relative z-1">
              <LoadingState $size="small" />
            </div>
          ) : (
            this.renderFetchMoreAction(dateObject)
          )}
        </>
      );
    }
    const { eventsFiltersApp } = this.props;
    const isThereAFilterActive = !isNoFilter(getDefaultFilter(eventsFiltersApp));

    return (
      <Body tw="text-center pt-16 pb-28" as="p" $color="gray dark" key={key}>
        {isThereAFilterActive
          ? i18n('No events found for the current filter.')
          : i18n('No events scheduled.')}
      </Body>
    );
  }

  renderEvents(dateObject, eventsStructure, key) {
    const infoCards = this.renderEventsOfDate(dateObject, eventsStructure, key);
    const stickyTop = this.calculateStickyPositionDayHeader();
    const { openDates, onDateTitleClick } = this.props;
    const { dateFormatSetting } = this.context;
    const initiallyCollapsed = !openDates.some((openDateObj) =>
      isOnSameDate(dateObject, openDateObj)
    );
    const label = printInShortFormatOrAsRelativeDate(dateObject, dateFormatSetting);

    const collapsibleDayEl = this.collapsibleDayElRefs.current.find((el) => {
      const dateObjDataAttribute = deserializeYesplanAPIDate(
        el.getAttribute('data-js-collapsible-listitem-fordate')
      );
      if (isOnSameDate(dateObjDataAttribute, dateObject)) {
        return el;
      }
    });

    return (
      <div
        key={`events-of-${dateObject}`}
        data-js-collapsible-listitem-fordate={serializeYesplanAPIDate(dateObject)}
        data-test-collapsible-listitem-fordate={serializeYesplanAPIDate(dateObject)}
        ref={this.addToCollapsibleDayRefs}
      >
        <CollapsibleListItem
          variant="primary"
          label={label}
          onClick={(collapsed) =>
            onDateTitleClick(collapsed, dateObject, collapsibleDayEl)
          }
          initiallyCollapsed={initiallyCollapsed}
          stickyTop={stickyTop}
          stickyHeader
          icon={false}
          dataAttributes={{
            date: dateObject
          }}
          tw="-mt-px"
          stickyHeaderRef={this.dayHeaderRef}
        >
          <div
            tw="pb-16 relative space-y-8"
            data-test-list="eventsPerDay"
            data-test-fordate={serializeYesplanAPIDate(dateObject)}
            data-js-wrapper-listitem-collapsible-content
            data-js-fordate={serializeYesplanAPIDate(dateObject)}
          >
            {infoCards}
          </div>
        </CollapsibleListItem>
      </div>
    );
  }

  renderContent() {
    const { dates, events } = this.props;
    return (
      <ContainerWrapper $relative tw="pl-0 pr-0 mt-2">
        <div data-test-events-week css="margin-bottom: 130px">
          {dates.map((dateObj) => {
            const eventsStructureOnDate = events[serializeYesplanAPIDate(dateObj)];
            return this.renderEvents(
              dateObj,
              eventsStructureOnDate,
              `events-${serializeYesplanAPIDate(dateObj)}`
            );
          })}
        </div>
      </ContainerWrapper>
    );
  }

  render() {
    return this.renderContent();
  }
}

EventsWeek.propTypes = {
  /** Dates inside the EventsWeek */
  dates: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  events: PropTypes.shape({}).isRequired,
  /** Initially all days inside the EventsWeek are collapsed, define which dates should be open */
  openDates: PropTypes.arrayOf(PropTypes.shape({})),
  onDateTitleClick: PropTypes.func,
  headerRef: PropTypes.shape({
    current: PropTypes.shape({})
  }),
  containerWrapperRef: PropTypes.shape({
    current: PropTypes.shape({})
  }),
  stickyPosition: PropTypes.number.isRequired,
  showMoreEventsOnDate: PropTypes.func.isRequired,
  hasDateMoreEventsToShow: PropTypes.func.isRequired,
  numberOfShownEventsPerDate: PropTypes.shape({}).isRequired,
  hasFetchMoreError: PropTypes.bool.isRequired,
  isFetchingMoreEvents: PropTypes.bool,
  numberOfNextEventsToShow: PropTypes.func.isRequired,
  eventsFiltersApp: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  eventsFilterOptions: PropTypes.shape({})
};

EventsWeek.defaultProps = {
  openDates: [todayDateObject()],
  onDateTitleClick: () => {},
  headerRef: null,
  containerWrapperRef: null,
  isFetchingMoreEvents: false,
  eventsFilterOptions: null
};

EventsWeek.contextType = FormatSettingsContext;

function ForwardRef(props, ref) {
  return <EventsWeek {...props} forwardedRef={ref} />;
}

export default React.forwardRef(ForwardRef);
